import { useState } from 'react'

import type { SaldoDeSobrante } from '@/lib/supabase/tipos-bd'
import { useSesion } from '@/modules/auth/useSesion'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'
import { fecha } from '@/shared/utilidades/formato'

import { AsignarAOrden } from './AsignarAOrden'
import { DarSalida } from './DarSalida'
import { cantidadLegible } from './useSobrantes'
import estilos from './PaginaSobrantes.module.css'

type Props = { saldos: SaldoDeSobrante[] }

/**
 * Lo que hay: un renglón por material, con su saldo. El saldo es lo que
 * entró (verificado) menos lo que salió; nunca baja de cero.
 */
export function InventarioDeSobrantes({ saldos }: Props) {
  const { puede } = useSesion()
  const [buscar, setBuscar] = useState('')
  const [agotados, setAgotados] = useState(false)
  const [abierto, setAbierto] = useState<number | null>(null)
  const [asignando, setAsignando] = useState(false)
  const [hecho, setHecho] = useState<string | null>(null)
  const puedeSacar = puede('sobrantes', 'editar')

  const t = buscar.trim().toLowerCase()
  const visibles = saldos
    .filter((s) => agotados || s.saldo > 0)
    .filter((s) => !t || s.descripcion.toLowerCase().includes(t))
  const hayAgotados = saldos.some((s) => s.saldo <= 0)

  if (saldos.length === 0) {
    return (
      <p className={estilos.vacio}>
        El inventario está vacío. Lo que se verifique en «Por verificar» se va acumulando aquí.
      </p>
    )
  }

  return (
    <div className={estilos.bloque}>
      <div className={estilos.barra}>
        <input
          className={estilos.buscador}
          type="search"
          placeholder="Buscar material"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          aria-label="Buscar material"
        />
        {puedeSacar && saldos.some((s) => s.saldo > 0) && !asignando && (
          <button
            type="button"
            className={`${estilos.botonFuerte} ${estilos.alFinal}`}
            onClick={() => {
              setAsignando(true)
              setAbierto(null)
              setHecho(null)
            }}
          >
            Asignar a una orden
          </button>
        )}
        {hayAgotados && (
          <label className={estilos.casilla}>
            <input type="checkbox" checked={agotados} onChange={(e) => setAgotados(e.target.checked)} />
            Ver también los agotados
          </label>
        )}
      </div>

      {hecho && <p className={estilos.hecho}>{hecho}</p>}
      {asignando && (
        <AsignarAOrden
          saldos={saldos}
          onCancelar={() => setAsignando(false)}
          onListo={(texto) => {
            setAsignando(false)
            setHecho(texto)
          }}
        />
      )}

      <div className={estilos.tablaCaja}>
        <table className={estilos.tabla}>
          <thead>
            <tr>
              <th>Material</th>
              <th className={estilos.num}>Hay</th>
              <th className={estilos.num}>Entró</th>
              <th className={estilos.num}>Salió</th>
              <th>Última entrada</th>
              {puedeSacar && <th aria-label="Acciones" />}
            </tr>
          </thead>
          <tbody>
            {visibles.map((s) => {
              const { codigo, nombre } = partirDescripcion(s.descripcion)
              const vivo = abierto === s.id_inventario
              return (
                <FilaDeSaldo
                  key={s.id_inventario}
                  saldo={s}
                  codigo={codigo}
                  nombre={nombre}
                  puedeSacar={puedeSacar}
                  abierto={vivo}
                  onAbrir={() => setAbierto(vivo ? null : s.id_inventario)}
                />
              )
            })}
            {visibles.length === 0 && (
              <tr>
                <td colSpan={puedeSacar ? 6 : 5} className={estilos.vacioFila}>
                  Ningún material con «{buscar}».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FilaDeSaldo({
  saldo: s,
  codigo,
  nombre,
  puedeSacar,
  abierto,
  onAbrir,
}: {
  saldo: SaldoDeSobrante
  codigo: string | null
  nombre: string
  puedeSacar: boolean
  abierto: boolean
  onAbrir: () => void
}) {
  return (
    <>
      <tr className={s.saldo <= 0 ? estilos.agotado : undefined}>
        <td>
          <span className={estilos.nombre}>{nombre}</span>
          <span className={estilos.detalle}>
            {codigo && <span className={estilos.codigo}>{codigo}</span>}
            de {s.ordenes} {s.ordenes === 1 ? 'orden' : 'órdenes'}
          </span>
        </td>
        <td className={`${estilos.num} ${estilos.saldo}`}>
          {cantidadLegible(s.saldo)} <span className={estilos.unidad}>{s.unidad ?? ''}</span>
        </td>
        <td className={estilos.num}>{cantidadLegible(s.entradas)}</td>
        <td className={estilos.num}>{s.salidas > 0 ? cantidadLegible(s.salidas) : '—'}</td>
        <td>{fecha(s.ultima_entrada)}</td>
        {puedeSacar && (
          <td className={estilos.accionCelda}>
            {s.saldo > 0 && (
              <button
                type="button"
                className={abierto ? estilos.botonFuerte : estilos.botonSuave}
                aria-expanded={abierto}
                onClick={onAbrir}
              >
                Dar salida
              </button>
            )}
          </td>
        )}
      </tr>
      {abierto && (
        <tr className={estilos.filaSalida}>
          <td colSpan={6}>
            <DarSalida saldo={s} nombre={nombre} onListo={onAbrir} />
          </td>
        </tr>
      )}
    </>
  )
}
